import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, Mail, MailOpen, Phone, Calendar, ChevronLeft, ChevronRight, Clock } from "lucide-react";

export default function AdminEmails() {
  const { token } = useAuth();
  const [emails, setEmails] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const limit = 20;

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchEmails = useCallback((nextPage, nextSearch) => {
    setLoading(true);
    const params = { skip: nextPage * limit, limit };
    if (nextSearch) params.search = nextSearch;
    axios.get(`${API}/admin/emails`, { headers, params })
      .then(r => { setEmails(r.data.emails); setTotal(r.data.total); setLoading(false); })
      .catch(() => setLoading(false));
  }, [headers, limit]);

  useEffect(() => { fetchEmails(page, search); }, [page, search, fetchEmails]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    fetchEmails(0, search);
  };

  const markAsRead = async (emailId) => {
    try {
      await axios.put(`${API}/admin/emails/${emailId}/read`, {}, { headers });
      fetchEmails(page, search);
      if (selectedEmail?.id === emailId) setSelectedEmail(prev => ({ ...prev, read: true }));
    } catch { /* silent */ }
  };

  const openEmail = (email) => {
    setSelectedEmail(email);
    if (!email.read) markAsRead(email.id);
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6" data-testid="admin-emails-page">
      <div>
        <h1 className="text-2xl font-semibold text-[#0D0D0D]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Emails</h1>
        <p className="text-sm text-[#4B5563]">All messages received through the contact form</p>
      </div>

      {/* Note about email functionality */}
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="p-4 flex items-start gap-3">
          <Clock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">Email Sending Not Yet Active</p>
            <p className="text-xs text-amber-700">All form submissions are stored here. Email notifications will be enabled once API keys are configured.</p>
          </div>
        </CardContent>
      </Card>

      {/* Search */}
      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search emails by name, email, or subject..."
            className="pl-9"
            data-testid="admin-emails-search-input"
          />
        </div>
        <Button type="submit" variant="outline">Search</Button>
      </form>

      <p className="text-sm text-[#4B5563]">{total} email{total !== 1 ? 's' : ''} total</p>

      {loading ? (
        <div className="space-y-2">{[1,2,3,4].map(i => <div key={i} className="h-14 bg-gray-100 rounded animate-pulse" />)}</div>
      ) : emails.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Mail className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-[#4B5563]">No emails yet</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border border-gray-100">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>From</TableHead>
                  <TableHead className="hidden md:table-cell">Subject</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="hidden sm:table-cell">Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {emails.map((email) => (
                  <TableRow
                    key={email.id}
                    className={`cursor-pointer hover:bg-gray-50 ${!email.read ? 'bg-blue-50/50 font-medium' : ''}`}
                    onClick={() => openEmail(email)}
                  >
                    <TableCell>
                      {email.read
                        ? <MailOpen className="w-4 h-4 text-gray-400" />
                        : <Mail className="w-4 h-4 text-blue-500" />
                      }
                    </TableCell>
                    <TableCell>
                      <p className="text-sm">{email.from_name}</p>
                      <p className="text-xs text-[#4B5563]">{email.from_email}</p>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <span className="text-xs text-[#4B5563]">{email.subject}</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">{email.status}</Badge>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <span className="text-xs text-[#4B5563]">{new Date(email.created_at).toLocaleDateString()}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); openEmail(email); }}>
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm text-[#4B5563]">Page {page + 1} of {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Email Detail Dialog */}
      <Dialog open={!!selectedEmail} onOpenChange={() => setSelectedEmail(null)}>
        <DialogContent className="max-w-lg" data-testid="admin-emails-drawer">
          <DialogHeader>
            <DialogTitle>{selectedEmail?.subject}</DialogTitle>
          </DialogHeader>
          {selectedEmail && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#0D0D0D]/10 rounded-full flex items-center justify-center">
                  <span className="text-sm font-semibold text-[#0D0D0D]">{selectedEmail.from_name?.charAt(0)?.toUpperCase()}</span>
                </div>
                <div>
                  <p className="font-semibold text-sm text-[#0D0D0D]">{selectedEmail.from_name}</p>
                  <p className="text-xs text-[#4B5563]">{selectedEmail.from_email}</p>
                </div>
                <Badge variant="outline" className="ml-auto text-xs">
                  {selectedEmail.read ? "Read" : "Unread"}
                </Badge>
              </div>

              <div className="space-y-2 bg-gray-50 rounded-lg p-4">
                <div className="flex items-center gap-2 text-xs text-[#4B5563]">
                  <Phone className="w-3 h-3" /> {selectedEmail.phone}
                </div>
                <div className="flex items-center gap-2 text-xs text-[#4B5563]">
                  <Calendar className="w-3 h-3" /> {new Date(selectedEmail.created_at).toLocaleString()}
                </div>
                <div className="flex items-center gap-2 text-xs text-[#4B5563]">
                  <Badge variant="outline" className="text-xs">{selectedEmail.service}</Badge>
                </div>
              </div>

              <div>
                <p className="text-xs text-[#4B5563] font-medium mb-1">Message</p>
                <p className="text-sm text-[#1F2937] bg-white border border-gray-100 rounded-lg p-4 leading-relaxed">
                  {selectedEmail.message}
                </p>
              </div>

              <div className="flex gap-2">
                <a href={`tel:${selectedEmail.phone}`} className="flex-1">
                  <Button className="w-full bg-[#97BF04] text-[#0D0D0D] hover:bg-[#83A603] gap-2">
                    <Phone className="w-4 h-4" /> Call Back
                  </Button>
                </a>
                <a href={`mailto:${selectedEmail.from_email}`} className="flex-1">
                  <Button variant="outline" className="w-full gap-2">
                    <Mail className="w-4 h-4" /> Reply
                  </Button>
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
